import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { fileSystemPersistence } from '../services/shared/fileSystemPersistence';

// Job Types
export type JobType = 'translation' | 'audit' | 'semantic-match' | 'quality-check';
export type JobStatus = 'running' | 'completed' | 'failed';

export interface JobUsage {
    inputTokens: number;
    outputTokens: number;
    totalTokens: number;
    cost: number;
}

export interface Job {
    id: string;
    type: JobType;
    fileId: string;
    fileName: string;
    status: JobStatus;
    progress: number;
    startTime: Date;
    endTime?: Date;
    error?: string;
    usage?: JobUsage;
    isRead: boolean; // For notification badge
}

interface JobManagerContextType {
    jobs: Job[];
    unreadCount: number;
    totalCost: number;
    addJob: (type: JobType, fileId: string, fileName: string) => string;
    updateJob: (jobId: string, updates: Partial<Job>) => void;
    completeJob: (jobId: string, usage?: JobUsage) => void;
    failJob: (jobId: string, error: string) => void;
    markAsRead: (jobId: string) => void;
    markAllAsRead: () => void;
    removeJob: (jobId: string) => void;
    clearHistory: () => void;
    clearCompleted: () => void;
    clearZombies: () => void;
    getJob: (jobId: string) => Job | undefined;
}

const JobManagerContext = createContext<JobManagerContextType | undefined>(undefined);

// Serialize/deserialize for disk
const serializeJobs = (jobs: Job[]): any[] => {
    return jobs.map(j => ({
        ...j,
        startTime: j.startTime.toISOString(),
        endTime: j.endTime?.toISOString()
    }));
};

const deserializeJobs = (data: any[]): Job[] => {
    try {
        return data.map((j: any) => ({
            ...j,
            startTime: new Date(j.startTime),
            endTime: j.endTime ? new Date(j.endTime) : undefined
        }));
    } catch {
        return [];
    }
};

export const JobManagerProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [jobs, setJobs] = useState<Job[]>([]);
    const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isInitializedRef = useRef(false);

    // Load jobs from File System on mount
    useEffect(() => {
        const loadJobs = async () => {
            if (!fileSystemPersistence.handle) return;
            try {
                const dir = await fileSystemPersistence.handle.getDirectoryHandle('jobs', { create: false });
                const fileHandle = await dir.getFileHandle('history.json', { create: false });
                const file = await fileHandle.getFile();
                const text = await file.text();
                const parsed = JSON.parse(text);
                if (Array.isArray(parsed)) {
                    const ZOMBIE_THRESHOLD_MS = 5 * 60 * 1000;
                    const now = Date.now();
                    const loaded = deserializeJobs(parsed).map(job => {
                        if (job.status === 'running') {
                            const runningTime = now - job.startTime.getTime();
                            if (runningTime > ZOMBIE_THRESHOLD_MS) {
                                return { ...job, status: 'failed' as JobStatus, endTime: new Date(), error: 'Interrupted - session ended before completion' };
                            }
                        }
                        return job;
                    });
                    setJobs(loaded);
                }
            } catch {
                // jobs/history.json doesn't exist yet - that's OK
            }
            isInitializedRef.current = true;
        };
        loadJobs();

        // Also try to clean up old localStorage data
        try { localStorage.removeItem('trans_bctn_jobs'); } catch { }
    }, []);

    // Save to File System on change (debounced 1s)
    useEffect(() => {
        if (!isInitializedRef.current) return; // Don't save before first load
        if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        saveTimerRef.current = setTimeout(async () => {
            if (!fileSystemPersistence.handle) return;
            try {
                await fileSystemPersistence.saveJson(['jobs'], 'history.json', serializeJobs(jobs));
            } catch (err) {
                console.warn('[JobManager] Failed to save jobs to disk:', err);
            }
        }, 1000);
        return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
    }, [jobs]);

    // Computed values
    const unreadCount = jobs.filter(j => !j.isRead && j.status !== 'running').length;
    const totalCost = jobs.reduce((sum, j) => sum + (j.usage?.cost || 0), 0);

    // Add new job
    const addJob = useCallback((type: JobType, fileId: string, fileName: string): string => {
        const id = `job-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const newJob: Job = {
            id,
            type,
            fileId,
            fileName,
            status: 'running',
            progress: 0,
            startTime: new Date(),
            isRead: false
        };
        setJobs(prev => [newJob, ...prev]);
        return id;
    }, []);

    // Update job
    const updateJob = useCallback((jobId: string, updates: Partial<Job>) => {
        setJobs(prev => prev.map(j => j.id === jobId ? { ...j, ...updates } : j));
    }, []);

    // Complete job
    const completeJob = useCallback((jobId: string, usage?: JobUsage) => {
        setJobs(prev => prev.map(j => j.id === jobId ? {
            ...j,
            status: 'completed' as JobStatus,
            progress: 100,
            endTime: new Date(),
            usage
        } : j));
    }, []);

    // Fail job
    const failJob = useCallback((jobId: string, error: string) => {
        setJobs(prev => prev.map(j => j.id === jobId ? {
            ...j,
            status: 'failed' as JobStatus,
            endTime: new Date(),
            error
        } : j));
    }, []);

    // Mark as read
    const markAsRead = useCallback((jobId: string) => {
        setJobs(prev => prev.map(j => j.id === jobId ? { ...j, isRead: true } : j));
    }, []);

    // Mark all as read
    const markAllAsRead = useCallback(() => {
        setJobs(prev => prev.map(j => ({ ...j, isRead: true })));
    }, []);

    // Remove job
    const removeJob = useCallback((jobId: string) => {
        setJobs(prev => prev.filter(j => j.id !== jobId));
    }, []);

    // Clear history (keep only running)
    const clearHistory = useCallback(() => {
        setJobs(prev => prev.filter(j => j.status === 'running'));
    }, []);

    // Clear completed jobs
    const clearCompleted = useCallback(() => {
        setJobs(prev => prev.filter(j => j.status !== 'completed'));
    }, []);

    // Clear zombie jobs (mark all "running" as failed)
    const clearZombies = useCallback(() => {
        setJobs(prev => prev.map(j => {
            if (j.status === 'running') {
                return {
                    ...j,
                    status: 'failed' as JobStatus,
                    endTime: new Date(),
                    error: 'Manually cleared - job was stale'
                };
            }
            return j;
        }));
    }, []);

    // Get job by ID
    const getJob = useCallback((jobId: string): Job | undefined => {
        return jobs.find(j => j.id === jobId);
    }, [jobs]);

    // Warn user before leaving if jobs are running
    useEffect(() => {
        const hasRunningJobs = jobs.some(j => j.status === 'running');

        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (hasRunningJobs) {
                e.preventDefault();
                e.returnValue = 'You have running jobs. They will be interrupted if you leave.';
                return e.returnValue;
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [jobs]);

    return (
        <JobManagerContext.Provider value={{
            jobs,
            unreadCount,
            totalCost,
            addJob,
            updateJob,
            completeJob,
            failJob,
            markAsRead,
            markAllAsRead,
            removeJob,
            clearHistory,
            clearCompleted,
            clearZombies,
            getJob
        }}>
            {children}
        </JobManagerContext.Provider>
    );
};

export const useJobManager = (): JobManagerContextType => {
    const context = useContext(JobManagerContext);
    if (!context) {
        throw new Error('useJobManager must be used within JobManagerProvider');
    }
    return context;
};
